import { WordData } from '../../../types/types';

export interface SigneDirecteurItem {
  key: string;
  word: WordData;
}

// Dedicated "Pratiquer" exercise for Kama: identify the signe directeur
// (the bi/trilitère phonogram carrying a word's phonetic core) across a
// handful of real, independently-verified corpus words — reusing the exact
// questions taught in the "Lire les hiéroglyphes" lesson (categories/
// lecture.ts) rather than duplicating the verification work. Every glyph
// here was cross-checked position-by-position against the Unicode Egyptian
// Hieroglyphs block (see the kama-hieroglyph-decoder skill) before being
// used — several other leadSign entries elsewhere in the corpus turned out
// to be mislabeled when checked this way, so nothing here is copied from
// those without that same check.
export const signeDirecteurPractice: SigneDirecteurItem[] = [
  {
    key: 'nfr',
    word: {
      translation: 'Le cœur phonétique d\'un mot',
      translationEn: 'The phonetic core of a word',
      illustration: '𓄤𓆑𓂋',
      leadSign: 'F35',
      reading: 'Lecture analytique — nfr (F35-I9-D21) : F35 est le signe directeur, I9 et D21 de simples compléments phonétiques qui en confirment la lecture.',
      readingEn: 'Analytical reading — nfr (F35-I9-D21): F35 is the lead sign, I9 and D21 mere phonetic complements confirming the reading.',
      quiz: {
        question: 'Dans ce groupe de signes 𓄤𓆑𓂋 (nfr), un seul porte à lui seul tout le poids phonétique du mot — les deux autres ne sont que des rappels. Lequel des trois est ce « signe directeur » ?',
        questionEn: 'In this group of signs 𓄤𓆑𓂋 (nfr), only one carries the word\'s entire phonetic weight on its own — the other two are mere reminders. Which of the three is this "lead sign"?',
        options: [
          'Le premier signe, 𓄤',
          'Le deuxième signe, 𓆑',
          'Le troisième signe, 𓂋',
          'Les trois à parts égales',
        ],
        optionsEn: [
          'The first sign, 𓄤',
          'The second sign, 𓆑',
          'The third sign, 𓂋',
          'All three equally',
        ],
        correctIndex: 0,
        explanation: 'Le signe directeur est le signe bilitère ou trilitère qui porte, à lui seul, le cœur phonétique du mot — par opposition aux signes unilitères qui l\'entourent (compléments phonétiques) et au déterminatif final, muet. Repérer le signe directeur d\'un mot, c\'est repérer la clé de sa lecture.',
        explanationEn: 'The lead sign is the biliteral or triliteral sign that alone carries the phonetic core of the word — as opposed to the uniliteral signs surrounding it (phonetic complements) and the final, silent determinative. Identifying a word\'s lead sign means identifying the key to its reading.',
      },
    },
  },
  {
    key: 'km',
    word: {
      translation: 'Noir (La Terre noire)',
      translationEn: 'Black (the Black Land)',
      illustration: '𓆎𓅓',
      quiz: {
        question: 'km, « noir » (𓆎𓅓) : lequel de ces deux signes est le signe directeur — celui qui porte, à lui seul, le cœur phonétique bilitère du mot ?',
        questionEn: 'km, "black" (𓆎𓅓): which of these two signs is the lead sign — the one that alone carries the word\'s biliteral phonetic core?',
        options: [
          '𓆎 (le premier signe)',
          '𓅓 (le second signe)',
        ],
        optionsEn: [
          '𓆎 (the first sign)',
          '𓅓 (the second sign)',
        ],
        correctIndex: 0,
        explanation: '𓆎 (I6, un morceau de peau de crocodile écailleuse) est un phonogramme bilitère qui porte à lui seul la valeur km : c\'est le signe directeur. 𓅓 (G17, la chouette) n\'est qu\'un complément phonétique unilitère, redondant, qui confirme la fin du mot. Km est la racine de Km.t, « Kemet, la Terre noire », le nom que les anciens Égyptiens donnaient à leur propre pays.',
        explanationEn: '𓆎 (I6, a piece of scaly crocodile skin) is a biliteral phonogram that alone carries the value km: it is the lead sign. 𓅓 (G17, the owl) is only a redundant uniliteral phonetic complement confirming the end of the word. Km is the root of Km.t, "Kemet, the Black Land", the name the ancient Egyptians gave to their own country.',
      },
    },
  },
  {
    key: 'Msḫnt',
    word: {
      translation: 'Meskhenet – Déesse de la naissance',
      translationEn: 'Meskhenet – Goddess of birth',
      illustration: '𓄟𓋴𓐍𓏏𓅆',
      quiz: {
        question: 'Msḫnt, « Meskhenet », déesse de la naissance (𓄟𓋴𓐍𓏏𓅆) : lequel de ces cinq signes est le signe directeur ?',
        questionEn: 'Msḫnt, "Meskhenet", goddess of birth (𓄟𓋴𓐍𓏏𓅆): which of these five signs is the lead sign?',
        options: [
          '𓄟 (le premier signe)',
          '𓋴 (le deuxième signe)',
          '𓐍 (le troisième signe)',
          '𓅆 (le dernier signe, un déterminatif)',
        ],
        optionsEn: [
          '𓄟 (the first sign)',
          '𓋴 (the second sign)',
          '𓐍 (the third sign)',
          '𓅆 (the last sign, a determinative)',
        ],
        correctIndex: 0,
        explanation: '𓄟 (F31, trois peaux de renard liées ensemble) porte la valeur bilitère ms, « mettre au monde, naître » : c\'est le signe directeur, celui qui donne son nom à Meskhenet, « celle de la brique d\'accouchement ». Les signes qui suivent complètent phonétiquement la lecture, et le dernier (une déesse assise) est le déterminatif final, muet.',
        explanationEn: '𓄟 (F31, three fox skins tied together) carries the biliteral value ms, "to give birth, to be born": it is the lead sign, the one that gives Meskhenet her name, "she of the birth brick". The following signs are phonetic complements confirming the reading, and the last one (a seated goddess) is the final, silent determinative.',
      },
    },
  },
  {
    key: 'Ỉmn',
    word: {
      translation: 'Amon – Dieu caché',
      translationEn: 'Amun – Hidden god',
      illustration: '𓇋𓏠𓈖𓀭',
      quiz: {
        question: 'Ỉmn, « Amon », le dieu caché (𓇋𓏠𓈖𓀭) : lequel de ces quatre signes est le signe directeur ?',
        questionEn: 'Ỉmn, "Amun", the hidden god (𓇋𓏠𓈖𓀭): which of these four signs is the lead sign?',
        options: [
          '𓇋 (le premier signe)',
          '𓏠 (le deuxième signe)',
          '𓈖 (le troisième signe)',
          '𓀭 (le dernier signe, un déterminatif)',
        ],
        optionsEn: [
          '𓇋 (the first sign)',
          '𓏠 (the second sign)',
          '𓈖 (the third sign)',
          '𓀭 (the last sign, a determinative)',
        ],
        correctIndex: 1,
        explanation: '𓏠 (Y5, un plateau de jeu) porte la valeur bilitère mn, « rester, être stable, être établi » : c\'est le signe directeur du nom d\'Amon. Le signe qui suit (𓈖, n) est un complément phonétique qui confirme la fin de cette valeur bilitère, et le dernier signe (un dieu assis) est le déterminatif final, muet.',
        explanationEn: '𓏠 (Y5, a game board) carries the biliteral value mn, "to remain, to be stable, to be established": it is the lead sign of Amun\'s name. The following sign (𓈖, n) is a phonetic complement confirming the end of this biliteral value, and the last sign (a seated god) is the final, silent determinative.',
      },
    },
  },
  {
    key: 'Nwt',
    word: {
      translation: 'Nout – Déesse du Ciel étoilé',
      translationEn: 'Nut – Goddess of the starry sky',
      illustration: '𓏌𓏏𓇯𓁐',
      quiz: {
        question: 'Nwt, « Nout », déesse du Ciel étoilé (𓏌𓏏𓇯𓁐) : lequel de ces quatre signes est le signe directeur ?',
        questionEn: 'Nwt, "Nut", goddess of the starry sky (𓏌𓏏𓇯𓁐): which of these four signs is the lead sign?',
        options: [
          '𓏌 (le premier signe)',
          '𓏏 (le deuxième signe)',
          '𓇯 (le troisième signe)',
          '𓁐 (le dernier signe, un déterminatif)',
        ],
        optionsEn: [
          '𓏌 (the first sign)',
          '𓏏 (the second sign)',
          '𓇯 (the third sign)',
          '𓁐 (the last sign, a determinative)',
        ],
        correctIndex: 0,
        explanation: '𓏌 (W24, un pot à eau) porte la valeur bilitère nw : c\'est le signe directeur du nom de Nout. Le signe qui suit précise soit un complément phonétique, soit la marque du féminin ; le troisième évoque le ciel lui-même, et le dernier (une femme assise) est le déterminatif de déesse, muet.',
        explanationEn: '𓏌 (W24, a water pot) carries the biliteral value nw: it is the lead sign of Nut\'s name. The following sign marks either a phonetic complement or the feminine ending; the third evokes the sky itself, and the last (a seated woman) is the goddess determinative, silent.',
      },
    },
  },
  {
    key: 'hrw',
    word: {
      translation: 'Jour – Le temps du soleil, de Rê',
      translationEn: 'Day – The time of the sun, of Ra',
      illustration: '𓉔𓅱𓇳',
      quiz: {
        question: 'hrw, « jour » (𓉔𓅱𓇳) : lequel de ces trois signes est le signe directeur ?',
        questionEn: 'hrw, "day" (𓉔𓅱𓇳): which of these three signs is the lead sign?',
        options: [
          '𓉔 (le premier signe)',
          '𓅱 (le deuxième signe)',
          '𓇳 (le dernier signe)',
        ],
        optionsEn: [
          '𓉔 (the first sign)',
          '𓅱 (the second sign)',
          '𓇳 (the last sign)',
        ],
        correctIndex: 2,
        explanation: '𓇳 (N5, disque solaire) porte à lui seul le sens de hrw, « jour » — une graphie courte où le disque solaire suffit à indiquer la lecture, sans même noter le r explicitement. Les deux signes précédents sont des compléments phonétiques. Pulaar hoore, « tête » ; wolof xuuy, « illuminer » ; bassa koyi, « apparaître » — la tête qui apparaît, le soleil qui se lève, portant le jour dans le même geste que la naissance.',
        explanationEn: '𓇳 (N5, solar disk) alone carries the meaning of hrw, "day" — a short spelling in which the solar disk suffices to indicate the reading, without even writing the r explicitly. The two preceding signs are phonetic complements. Pulaar hoore, "head"; Wolof xuuy, "to shine, illuminate"; Bassa koyi, "to appear" — the head that appears, the sun that rises, carrying day within the same gesture as birth.',
      },
    },
  },
  {
    key: 'Ḥr',
    word: {
      translation: 'Horus – Dieu faucon, protecteur du pharaon',
      translationEn: 'Horus – Falcon god, protector of the pharaoh',
      illustration: '𓎛𓁷𓂋𓅆',
      quiz: {
        question: 'Ḥr, « Horus » (𓎛𓁷𓂋𓅆) : lequel de ces quatre signes est le signe directeur ?',
        questionEn: 'Ḥr, "Horus" (𓎛𓁷𓂋𓅆): which of these four signs is the lead sign?',
        options: [
          '𓎛 (le premier signe)',
          '𓁷 (le deuxième signe)',
          '𓂋 (le troisième signe)',
          '𓅆 (le dernier signe, un déterminatif)',
        ],
        optionsEn: [
          '𓎛 (the first sign)',
          '𓁷 (the second sign)',
          '𓂋 (the third sign)',
          '𓅆 (the last sign, a determinative)',
        ],
        correctIndex: 1,
        explanation: '𓁷 (D2, un visage de face) porte la racine ḥr, « tête, visage, ce qui est en haut » — c\'est le signe directeur du nom d\'Horus, le dieu d\'en haut, le dieu du ciel. Le copte ϩⲗⲉ, « visage », en est le descendant direct. Le dernier signe (un faucon sur un support) est le déterminatif divin.',
        explanationEn: '𓁷 (D2, a face seen from the front) carries the root ḥr, "head, face, that which is above" — it is the lead sign of Horus\'s name, the god of on high, the god of the sky. Coptic ϩⲗⲉ, "face", is its direct descendant. The last sign (a falcon on a standard) is the divine determinative.',
      },
    },
  },
  {
    key: 'sp tpy',
    word: {
      translation: 'La Création, la Première Occasion (Zep Tepi)',
      translationEn: 'The Creation, the First Occasion (Zep Tepi)',
      illustration: '𓊃𓊪𓊗𓁶𓊪𓏭',
      quiz: {
        question: 'sp tpy, « la Première Occasion », le moment de la Création (𓊃𓊪𓊗𓁶𓊪𓏭) : lequel de ces six signes est le signe directeur ?',
        questionEn: 'sp tpy, "the First Occasion", the moment of Creation (𓊃𓊪𓊗𓁶𓊪𓏭): which of these six signs is the lead sign?',
        options: [
          'Le premier signe, 𓊃',
          'Le deuxième signe, 𓊪',
          'Le quatrième signe, 𓁶',
          'Le dernier signe, 𓏭',
        ],
        optionsEn: [
          'The first sign, 𓊃',
          'The second sign, 𓊪',
          'The fourth sign, 𓁶',
          'The last sign, 𓏭',
        ],
        correctIndex: 2,
        explanation: '𓁶 (D1, une tête vue de profil) porte la valeur tp, « tête, premier » : c\'est le signe directeur de tpy, « premier », le second mot de l\'expression. Il est précédé de sp (« fois, occasion ») et suivi de compléments phonétiques. sp tpy, littéralement « la première fois/occasion », désigne le moment fondateur de la Création dans la tradition égyptienne.',
        explanationEn: '𓁶 (D1, a head seen in profile) carries the value tp, "head, first": it is the lead sign of tpy, "first", the second word of the expression. It is preceded by sp ("time, occasion") and followed by phonetic complements. sp tpy, literally "the first time/occasion", designates the founding moment of Creation in Egyptian tradition.',
      },
    },
  },
  {
    key: 'sbꜣ',
    word: {
      translation: 'Étoile',
      translationEn: 'Star',
      illustration: '𓋴𓃀𓇼𓏺',
      quiz: {
        question: 'sbꜣ, « étoile » (𓋴𓃀𓇼𓏺) : lequel de ces quatre signes est le signe directeur ?',
        questionEn: 'sbꜣ, "star" (𓋴𓃀𓇼𓏺): which of these four signs is the lead sign?',
        options: [
          '𓋴 (le premier signe)',
          '𓃀 (le deuxième signe)',
          '𓇼 (le troisième signe)',
          '𓏺 (le dernier signe)',
        ],
        optionsEn: [
          '𓋴 (the first sign)',
          '𓃀 (the second sign)',
          '𓇼 (the third sign)',
          '𓏺 (the last sign)',
        ],
        correctIndex: 2,
        explanation: '𓇼 (N14, une étoile à cinq branches) porte le sens à lui seul : c\'est le signe directeur de sbꜣ, « étoile ». Le même signe sert aussi, seul, à écrire le nombre « cinq » (voir la fiche « djw » dans Nombres). Le copte ⲥⲓⲟⲩ, « étoile », en est le descendant direct.',
        explanationEn: '𓇼 (N14, a five-pointed star) carries the meaning on its own: it is the lead sign of sbꜣ, "star". The same sign is also used, alone, to write the number "five" (see the "djw" entry in Numbers). Coptic ⲥⲓⲟⲩ, "star", is its direct descendant.',
      },
    },
  },
  {
    key: 'šnw-cartouche',
    word: {
      translation: 'Cartouche – Anneau-šen étiré entourant les noms royaux',
      translationEn: 'Cartouche – Elongated šen ring surrounding royal names',
      illustration: '𓍲𓈖𓏌𓅱𓍶',
      quiz: {
        question: 'šnw, « cartouche » (𓍲𓈖𓏌𓅱𓍶) — le mot même pour l\'anneau qui entourait le nom de Ptolémée sur la pierre de Rosette : lequel de ces cinq signes est le signe directeur ?',
        questionEn: 'šnw, "cartouche" (𓍲𓈖𓏌𓅱𓍶) — the very word for the ring that surrounded Ptolemy\'s name on the Rosetta Stone: which of these five signs is the lead sign?',
        options: [
          'Le premier signe, 𓍲',
          'Le deuxième signe, 𓈖',
          'Le troisième signe, 𓏌',
          'Le dernier signe, 𓍶',
        ],
        optionsEn: [
          'The first sign, 𓍲',
          'The second sign, 𓈖',
          'The third sign, 𓏌',
          'The last sign, 𓍶',
        ],
        correctIndex: 3,
        explanation: '𓍶 (V9, l\'anneau-šen lui-même) porte le sens à lui seul : c\'est le signe directeur de šnw, « cartouche », qui signifie aussi « éternité » ou « circuit, circonférence ». C\'est cet anneau, étiré en ovale, qui entourait les noms royaux — dont celui de Ptolémée, déchiffré par Champollion (voir la fiche « Ptwlmys » dans Lire les hiéroglyphes).',
        explanationEn: '𓍶 (V9, the šen-ring itself) carries the meaning on its own: it is the lead sign of šnw, "cartouche", which also means "eternity" or "circuit, circumference". It is this ring, stretched into an oval, that surrounded royal names — including that of Ptolemy, deciphered by Champollion (see the "Ptwlmys" entry in Reading Hieroglyphs).',
      },
    },
  },
  {
    key: 'Ỉtm',
    word: {
      translation: 'Atoum – Dieu du soleil couchant, créateur primordial',
      translationEn: 'Atum – God of the setting sun, primordial creator',
      illustration: '𓇋𓍃𓅓𓀭',
      quiz: {
        question: 'Ỉtm, « Atoum » (𓇋𓍃𓅓𓀭) : lequel de ces quatre signes est le signe directeur ?',
        questionEn: 'Ỉtm, "Atum" (𓇋𓍃𓅓𓀭): which of these four signs is the lead sign?',
        options: [
          '𓇋 (le premier signe)',
          '𓍃 (le deuxième signe)',
          '𓅓 (le troisième signe)',
          '𓀭 (le dernier signe, un déterminatif)',
        ],
        optionsEn: [
          '𓇋 (the first sign)',
          '𓍃 (the second sign)',
          '𓅓 (the third sign)',
          '𓀭 (the last sign, a determinative)',
        ],
        correctIndex: 1,
        explanation: '𓍃 (U15, un traîneau) porte la valeur tm : c\'est le signe directeur du nom d\'Atoum. Ce même signe est aussi le radical de tmt, « traîneau » (voir la fiche « tmt » dans Objets), et rejoint un vaste champ « houe, creuser, cultiver » en Afrique bantoue.',
        explanationEn: '𓍃 (U15, a sledge) carries the value tm: it is the lead sign of Atum\'s name. This same sign is also the root of tmt, "sledge" (see the "tmt" entry in Objects), and joins a broad "hoe, dig, cultivate" field across Bantu Africa.',
      },
    },
  },
  {
    key: 'Ḏḥwtj',
    word: {
      translation: 'Thoth – Dieu de la sagesse, de l\'écriture et de la lune',
      translationEn: 'Thoth – God of wisdom, writing and the moon',
      illustration: '𓂧𓎛𓅝𓏏𓏭',
      quiz: {
        question: 'Ḏḥwtj, « Thot » (𓂧𓎛𓅝𓏏𓏭) : lequel de ces cinq signes est le signe directeur ?',
        questionEn: 'Ḏḥwtj, "Thoth" (𓂧𓎛𓅝𓏏𓏭): which of these five signs is the lead sign?',
        options: [
          'Le premier signe, 𓂧',
          'Le deuxième signe, 𓎛',
          'Le troisième signe, 𓅝',
          'Le dernier signe, 𓏭',
        ],
        optionsEn: [
          'The first sign, 𓂧',
          'The second sign, 𓎛',
          'The third sign, 𓅝',
          'The last sign, 𓏭',
        ],
        correctIndex: 2,
        explanation: '𓅝 (G26, un ibis) porte la racine ḏḥw : c\'est le signe directeur du nom de Thot, dieu à tête d\'ibis. Cette même racine signifie aussi « cigogne, grue » (voir la fiche « ḏḥw » dans Animaux) — de grands oiseaux échassiers apparentés par leur allure.',
        explanationEn: '𓅝 (G26, an ibis) carries the root ḏḥw: it is the lead sign of Thoth\'s name, the ibis-headed god. This same root also means "stork, crane" (see the "ḏḥw" entry in Animals) — large wading birds related by their appearance.',
      },
    },
  },
  {
    key: 'ṯꜣw',
    word: {
      translation: 'Air, vent, souffle',
      translationEn: 'Air, wind, breath',
      illustration: '𓅷𓅱𓊡',
      quiz: {
        question: 'ṯꜣw, « le vent, le souffle » (𓅷𓅱𓊡) : lequel de ces trois signes est le signe directeur ?',
        questionEn: 'ṯꜣw, "wind, breath" (𓅷𓅱𓊡): which of these three signs is the lead sign?',
        options: [
          '𓅷 (le premier signe)',
          '𓅱 (le deuxième signe)',
          '𓊡 (le dernier signe, un déterminatif)',
        ],
        optionsEn: [
          '𓅷 (the first sign)',
          '𓅱 (the second sign)',
          '𓊡 (the last sign, a determinative)',
        ],
        correctIndex: 0,
        explanation: '𓅷 (P5, une voile gonflée par le vent) porte le sens à lui seul : c\'est le signe directeur de ṯꜣw, « air, vent, souffle » — une image directement parlante, celle du vent qui gonfle une voile de bateau. Le copte ⲧⲏⲩ, « vent », en est le descendant direct.',
        explanationEn: '𓅷 (P5, a sail filled with wind) carries the meaning on its own: it is the lead sign of ṯꜣw, "air, wind, breath" — a directly evocative image, that of the wind filling a boat\'s sail. Coptic ⲧⲏⲩ, "wind", is its direct descendant.',
      },
    },
  },
  {
    key: 'Wsỉr',
    word: {
      translation: 'Osiris – Dieu de la mort et de la résurrection',
      translationEn: 'Osiris – God of death and resurrection',
      illustration: '𓊨𓁹',
      quiz: {
        question: 'Wsỉr, « Osiris » (𓊨𓁹) : lequel de ces deux signes est le signe directeur ?',
        questionEn: 'Wsỉr, "Osiris" (𓊨𓁹): which of these two signs is the lead sign?',
        options: [
          '𓊨 (le premier signe)',
          '𓁹 (le second signe, un déterminatif)',
        ],
        optionsEn: [
          '𓊨 (the first sign)',
          '𓁹 (the second sign, a determinative)',
        ],
        correctIndex: 0,
        explanation: '𓊨 (Q1, un trône) porte le sens à lui seul : c\'est le signe directeur du nom d\'Osiris. Ce même signe est aussi un mot autonome, st.t, « trône » (voir la fiche « st.t » dans Objets) — le trône, symbole de la royauté qu\'Osiris incarne comme roi des morts.',
        explanationEn: '𓊨 (Q1, a throne) carries the meaning on its own: it is the lead sign of Osiris\'s name. This same sign is also a standalone word, st.t, "throne" (see the "st.t" entry in Objects) — the throne, symbol of the kingship Osiris embodies as king of the dead.',
      },
    },
  },
  {
    key: 'ḏḥw',
    word: {
      translation: 'Cigogne, grue',
      translationEn: 'Stork, crane',
      illustration: '𓂧𓎛𓅝',
      quiz: {
        question: 'ḏḥw, « cigogne, grue » (𓂧𓎛𓅝) : lequel de ces trois signes est le signe directeur ?',
        questionEn: 'ḏḥw, "stork, crane" (𓂧𓎛𓅝): which of these three signs is the lead sign?',
        options: ['𓂧 (le premier signe)', '𓎛 (le deuxième signe)', '𓅝 (le dernier signe)'],
        optionsEn: ['𓂧 (the first sign)', '𓎛 (the second sign)', '𓅝 (the last sign)'],
        correctIndex: 2,
        explanation: '𓅝 (G26, un ibis) est le signe directeur — la racine même du nom du dieu Thot (voir la fiche « Ḏḥwtj » dans Divinités). Bamanan dyugõ, « calao » ; maninka dùwa, « vautour » — un large champ de grands oiseaux échassiers ou charognards partageant cette racine.',
        explanationEn: '𓅝 (G26, an ibis) is the lead sign — the very root of the name of the god Thoth (see the "Ḏḥwtj" entry in Divinities). Bamanan dyugõ, "hornbill"; Maninka dùwa, "vulture" — a broad field of large wading or scavenging birds sharing this root.',
      },
    },
  },
  {
    key: 'db',
    word: {
      translation: 'Hippopotame',
      translationEn: 'Hippopotamus',
      illustration: '𓄏𓃀𓃯',
      quiz: {
        question: 'db, « hippopotame » (𓄏𓃀𓃯) : lequel de ces trois signes est le signe directeur ?',
        questionEn: 'db, "hippopotamus" (𓄏𓃀𓃯): which of these three signs is the lead sign?',
        options: ['𓄏 (le premier signe)', '𓃀 (le deuxième signe)', '𓃯 (le dernier signe, un déterminatif)'],
        optionsEn: ['𓄏 (the first sign)', '𓃀 (the second sign)', '𓃯 (the last sign, a determinative)'],
        correctIndex: 0,
        explanation: '𓄏 (F16, une corne) est le signe directeur : db est un homographe exact de « corne » (F16-D58, sans le déterminatif hippopotame). Beja daab, « corne » ; pulaar diba, « nom d\'honneur de l\'hippopotame ».',
        explanationEn: '𓄏 (F16, a horn) is the lead sign: db is an exact homograph of "horn" (F16-D58, without the hippopotamus determinative). Beja daab, "horn"; Pulaar diba, "honorific name of the hippopotamus".',
      },
    },
  },
  {
    key: 'sw',
    word: {
      translation: 'Porc',
      translationEn: 'Pig',
      illustration: '𓆄𓅱𓃟',
      quiz: {
        question: 'sw, « porc » (𓆄𓅱𓃟) : lequel de ces trois signes est le signe directeur ?',
        questionEn: 'sw, "pig" (𓆄𓅱𓃟): which of these three signs is the lead sign?',
        options: ['𓆄 (le premier signe)', '𓅱 (le deuxième signe)', '𓃟 (le dernier signe, un déterminatif)'],
        optionsEn: ['𓆄 (the first sign)', '𓅱 (the second sign)', '𓃟 (the last sign, a determinative)'],
        correctIndex: 0,
        explanation: '𓆄 (H6, une plume) est le signe directeur — le même signe que dans le nom du dieu Šw (Chou). Copte ⲱⲉ, « cochon, porc » ; malinké say/sey, « phacochère ».',
        explanationEn: '𓆄 (H6, a feather) is the lead sign — the same sign found in the name of the god Šw (Shu). Coptic ⲱⲉ, "pig, swine"; Malinke say/sey, "warthog".',
      },
    },
  },
  {
    key: 'šspw',
    word: {
      translation: 'Sphinx',
      translationEn: 'Sphinx',
      illustration: '𓊐𓊪𓅱',
      quiz: {
        question: 'šspw, « sphinx » (𓊐𓊪𓅱) : lequel de ces trois signes est le signe directeur ?',
        questionEn: 'šspw, "sphinx" (𓊐𓊪𓅱): which of these three signs is the lead sign?',
        options: ['𓊐 (le premier signe)', '𓊪 (le deuxième signe)', '𓅱 (le dernier signe)'],
        optionsEn: ['𓊐 (the first sign)', '𓊪 (the second sign)', '𓅱 (the last sign)'],
        correctIndex: 0,
        explanation: '𓊐 (O43) porte à lui seul la valeur trilitère šsp : c\'est le signe directeur. Soninké sáppà, wolof sàppe, mandinka sápu, bambara sáfa, « alignement, rangée, rang » — un champ qui évoque l\'alignement des sphinx bordant les allées des temples.',
        explanationEn: '𓊐 (O43) alone carries the triliteral value šsp: it is the lead sign. Soninke sáppà, Wolof sàppe, Mandinka sápu, Bambara sáfa, "alignment, row, rank" — a field evoking the alignment of sphinxes lining temple avenues.',
      },
    },
  },
  {
    key: 'rmn',
    word: {
      translation: 'Épaule',
      translationEn: 'Shoulder',
      illustration: '𓂋𓏠𓈖𓂢',
      quiz: {
        question: 'rmn, « épaule » (𓂋𓏠𓈖𓂢) : lequel de ces quatre signes est le signe directeur ?',
        questionEn: 'rmn, "shoulder" (𓂋𓏠𓈖𓂢): which of these four signs is the lead sign?',
        options: ['𓂋 (le premier signe)', '𓏠 (le deuxième signe)', '𓈖 (le troisième signe)', '𓂢 (le dernier signe, un déterminatif)'],
        optionsEn: ['𓂋 (the first sign)', '𓏠 (the second sign)', '𓈖 (the third sign)', '𓂢 (the last sign, a determinative)'],
        correctIndex: 1,
        explanation: '𓏠 (Y5, un plateau de jeu) porte la valeur bilitère mn : c\'est le signe directeur de rmn, « épaule ». Bambara kàman, kàma ; malinké kàman, « épaule, clavicule ».',
        explanationEn: '𓏠 (Y5, a game board) carries the biliteral value mn: it is the lead sign of rmn, "shoulder". Bambara kàman, kàma; Malinke kàman, "shoulder, collarbone".',
      },
    },
  },
  {
    key: 'šnw (cheveux)',
    word: {
      translation: 'Cheveux',
      translationEn: 'Hair',
      illustration: '𓍲𓈖𓏌𓏲',
      quiz: {
        question: 'šnw, « cheveux » (𓍲𓈖𓏌𓏲) : lequel de ces quatre signes est le signe directeur ?',
        questionEn: 'šnw, "hair" (𓍲𓈖𓏌𓏲): which of these four signs is the lead sign?',
        options: ['𓍲 (le premier signe)', '𓈖 (le deuxième signe)', '𓏌 (le troisième signe)', '𓏲 (le dernier signe)'],
        optionsEn: ['𓍲 (the first sign)', '𓈖 (the second sign)', '𓏌 (the third sign)', '𓏲 (the last sign)'],
        correctIndex: 0,
        explanation: '𓍲 (V7, valeur šn) est le signe directeur : ce mot est un homographe de šnw, « cartouche », et šnw, « acacia ». Bambara sáni/sáani, malinké sándi/sánti, soninké sénta/sénti, « peigner ».',
        explanationEn: '𓍲 (V7, value šn) is the lead sign: this word is a homograph of šnw, "cartouche", and šnw, "acacia". Bambara sáni/sáani, Malinke sándi/sánti, Soninke sénta/sénti, "to comb".',
      },
    },
  },
  {
    key: 'Šw',
    word: {
      translation: 'Chou – Dieu de l\'air et de la lumière',
      translationEn: 'Shu – God of air and light',
      illustration: '𓆄𓅱𓀭',
      quiz: {
        question: 'Šw, « Chou », dieu de l\'air (𓆄𓅱𓀭) : lequel de ces trois signes est le signe directeur ?',
        questionEn: 'Šw, "Shu", god of the air (𓆄𓅱𓀭): which of these three signs is the lead sign?',
        options: ['𓆄 (le premier signe)', '𓅱 (le deuxième signe)', '𓀭 (le dernier signe, un déterminatif)'],
        optionsEn: ['𓆄 (the first sign)', '𓅱 (the second sign)', '𓀭 (the last sign, a determinative)'],
        correctIndex: 0,
        explanation: '𓆄 (H6, une plume) est le signe directeur — le même signe que dans sw, « porc » (voir la fiche « sw » dans Animaux). Copte ⲱⲉ, « cochon, porc » ; kikongo siwu, « saison sèche ».',
        explanationEn: '𓆄 (H6, a feather) is the lead sign — the same sign found in sw, "pig" (see the "sw" entry in Animals). Coptic ⲱⲉ, "pig, swine"; Kikongo siwu, "dry season".',
      },
    },
  },
  {
    key: 'iwn',
    word: {
      translation: 'An, Ani – Épithète d\'Osiris',
      translationEn: 'An, Ani – Epithet of Osiris',
      illustration: '𓉺𓏤𓀭',
      quiz: {
        question: 'iwn, « An/Ani », épithète d\'Osiris (𓉺𓏤𓀭) : lequel de ces trois signes est le signe directeur ?',
        questionEn: 'iwn, "An/Ani", epithet of Osiris (𓉺𓏤𓀭): which of these three signs is the lead sign?',
        options: ['𓉺 (le premier signe)', '𓏤 (le deuxième signe)', '𓀭 (le dernier signe, un déterminatif)'],
        optionsEn: ['𓉺 (the first sign)', '𓏤 (the second sign)', '𓀭 (the last sign, a determinative)'],
        correctIndex: 0,
        explanation: '𓉺 (O28, un pilier) est le signe directeur — un mot autonome dans le champ « colonne, pilier » : wolof jën, « colonne » ; dogon jéyⁿ/jɛ̃ⁿ/zèyⁿ, « pilier ». Ce même signe épelle aussi iwny, « miroir » (voir la fiche « iwny » dans Objets).',
        explanationEn: '𓉺 (O28, a pillar) is the lead sign — a standalone word in the "column, pillar" field: Wolof jën, "column"; Dogon jéyⁿ/jɛ̃ⁿ/zèyⁿ, "pillar". This same sign also spells iwny, "mirror" (see the "iwny" entry in Objects).',
      },
    },
  },
  {
    key: 'Ỉs.t',
    word: {
      translation: 'Isis – Grande Déesse mère et de la magie',
      translationEn: 'Isis – Great mother goddess of magic',
      illustration: '𓊨𓏏𓁐',
      quiz: {
        question: 'Ỉs.t, « Isis » (𓊨𓏏𓁐) : lequel de ces trois signes est le signe directeur ?',
        questionEn: 'Ỉs.t, "Isis" (𓊨𓏏𓁐): which of these three signs is the lead sign?',
        options: ['𓊨 (le premier signe)', '𓏏 (le deuxième signe)', '𓁐 (le dernier signe, un déterminatif)'],
        optionsEn: ['𓊨 (the first sign)', '𓏏 (the second sign)', '𓁐 (the last sign, a determinative)'],
        correctIndex: 0,
        explanation: '𓊨 (Q1, un trône) est le signe directeur — le même que dans Wsỉr, « Osiris » (voir la fiche « Wsỉr » dans Divinités), et le mot autonome st.t, « trône » (voir la fiche « st.t » dans Objets). Bamanan sigi, « s\'asseoir », sigilan, « siège ».',
        explanationEn: '𓊨 (Q1, a throne) is the lead sign — the same one found in Wsỉr, "Osiris" (see the "Wsỉr" entry in Divinities), and the standalone word st.t, "throne" (see the "st.t" entry in Objects). Bamanan sigi, "to sit down", sigilan, "seat".',
      },
    },
  },
  {
    key: 'Ḥr-variante',
    word: {
      translation: 'Horus (variante du nom, sur la racine ḥr)',
      translationEn: 'Horus (variant of the name, on the root ḥr)',
      illustration: '𓁷𓂋𓏏𓈐',
      quiz: {
        question: 'Ḥr, variante graphique du nom d\'Horus (𓁷𓂋𓏏𓈐) : lequel de ces quatre signes est le signe directeur ?',
        questionEn: 'Ḥr, a graphic variant of Horus\'s name (𓁷𓂋𓏏𓈐): which of these four signs is the lead sign?',
        options: ['𓁷 (le premier signe)', '𓂋 (le deuxième signe)', '𓏏 (le troisième signe)', '𓈐 (le dernier signe)'],
        optionsEn: ['𓁷 (the first sign)', '𓂋 (the second sign)', '𓏏 (the third sign)', '𓈐 (the last sign)'],
        correctIndex: 0,
        explanation: '𓁷 (D2, un visage de face) est le signe directeur — la même racine ḥr que dans la fiche « Ḥr » vue précédemment, ici épelée avec des signes complémentaires différents.',
        explanationEn: '𓁷 (D2, a face seen from the front) is the lead sign — the same root ḥr seen earlier in the "Ḥr" entry, here spelled with different complementary signs.',
      },
    },
  },
  {
    key: 'Ḥr-pꜣ-ẖrd',
    word: {
      translation: 'Harpocrate – « Horus l\'enfant », fils posthume d\'Isis et Osiris',
      translationEn: 'Harpocrates – "Horus the child", posthumous son of Isis and Osiris',
      illustration: '𓅃𓏺𓊪𓄡𓂋𓂧𓀔',
      quiz: {
        question: 'Ḥr-pꜣ-ẖrd, « Harpocrate », Horus enfant (𓅃𓏺𓊪𓄡𓂋𓂧𓀔) : lequel de ces sept signes est le signe directeur ?',
        questionEn: 'Ḥr-pꜣ-ẖrd, "Harpocrates", Horus the child (𓅃𓏺𓊪𓄡𓂋𓂧𓀔): which of these seven signs is the lead sign?',
        options: ['Le premier signe, 𓅃', 'Le troisième signe, 𓊪', 'Le cinquième signe, 𓂋', 'Le dernier signe, 𓀔'],
        optionsEn: ['The first sign, 𓅃', 'The third sign, 𓊪', 'The fifth sign, 𓂋', 'The last sign, 𓀔'],
        correctIndex: 0,
        explanation: '𓅃 (G5, un faucon), logogramme d\'Horus lui-même, est le signe directeur : la même divinité que dans la fiche « Ḥr » (pulaar hoore, « tête » ; susu kóóréé, « firmament, ciel »), ici combinée avec pꜣ (« le ») et ẖrd (« enfant ») pour former « Horus l\'enfant ».',
        explanationEn: '𓅃 (G5, a falcon), a logogram for Horus himself, is the lead sign: the same deity as in the "Ḥr" entry (Pulaar hoore, "head"; Susu kóóréé, "firmament, sky"), here combined with pꜣ ("the") and ẖrd ("child") to form "Horus the child".',
      },
    },
  },
  {
    key: 'wnty',
    word: {
      translation: 'Ounty – « Horus le Triomphateur »',
      translationEn: 'Wenty – "Horus the Triumphant"',
      illustration: '𓃹𓏏𓏭𓇼𓇳',
      quiz: {
        question: 'wnty, « Ounty », aspect vengeur d\'Horus (𓃹𓏏𓏭𓇼𓇳) : lequel de ces cinq signes est le signe directeur ?',
        questionEn: 'wnty, "Wenty", a vengeful aspect of Horus (𓃹𓏏𓏭𓇼𓇳): which of these five signs is the lead sign?',
        options: ['𓃹 (le premier signe)', '𓏏 (le deuxième signe)', '𓏭 (le troisième signe)', '𓇳 (le dernier signe)'],
        optionsEn: ['𓃹 (the first sign)', '𓏏 (the second sign)', '𓏭 (the third sign)', '𓇳 (the last sign)'],
        correctIndex: 0,
        explanation: '𓃹 (E34, un lièvre, valeur wn) est le signe directeur. Ce mot est homophone de wnwt, « heure » (voir la fiche « wnw.t » dans Temps) — pulaar yonude, « avoir le temps de faire quelque chose ».',
        explanationEn: '𓃹 (E34, a hare, value wn) is the lead sign. This word is homophonous with wnwt, "hour" (see the "wnw.t" entry in Time) — Pulaar yonude, "to have time to do something".',
      },
    },
  },
  {
    key: 'nmw',
    word: {
      translation: 'Nain',
      translationEn: 'Dwarf',
      illustration: '𓈖𓈗',
      quiz: {
        question: 'nmw, « nain » (𓈖𓈗) : lequel de ces deux signes est le signe directeur ?',
        questionEn: 'nmw, "dwarf" (𓈖𓈗): which of these two signs is the lead sign?',
        options: ['𓈖 (le premier signe)', '𓈗 (le second signe)'],
        optionsEn: ['𓈖 (the first sign)', '𓈗 (the second sign)'],
        correctIndex: 1,
        explanation: '𓈗 (N35A), avec le signe précédent, porte la valeur nmw, « nain » — un mot bâti entièrement sur des signes d\'eau déjà rencontrés ailleurs dans le corpus.',
        explanationEn: '𓈗 (N35A), together with the preceding sign, carries the value nmw, "dwarf" — a word built entirely from water signs already encountered elsewhere in the corpus.',
      },
    },
  },
  {
    key: 'wꜣr',
    word: {
      translation: 'Corde',
      translationEn: 'Rope',
      illustration: '𓍯𓄿𓏏𓂋𓏏𓍢',
      quiz: {
        question: 'wꜣr, « corde » (𓍯𓄿𓏏𓂋𓏏𓍢) : lequel de ces six signes est le signe directeur ?',
        questionEn: 'wꜣr, "rope" (𓍯𓄿𓏏𓂋𓏏𓍢): which of these six signs is the lead sign?',
        options: ['Le premier signe, 𓍯', 'Le deuxième signe, 𓄿', 'Le quatrième signe, 𓂋', 'Le dernier signe, 𓍢'],
        optionsEn: ['The first sign, 𓍯', 'The second sign, 𓄿', 'The fourth sign, 𓂋', 'The last sign, 𓍢'],
        correctIndex: 0,
        explanation: '𓍯 (V4, une corde ou un lasso, valeur wꜣ) est le signe directeur. Wolof waru, « attache de sandale, lanière de sandale » — une lanière tressée, comme la corde égyptienne elle-même.',
        explanationEn: '𓍯 (V4, a rope or lasso, value wꜣ) is the lead sign. Wolof waru, "sandal strap, sandal thong" — a braided strap, like the Egyptian rope itself.',
      },
    },
  },
  {
    key: 'iwny',
    word: {
      translation: 'Miroir',
      translationEn: 'Mirror',
      illustration: '𓉺𓇋𓇋',
      quiz: {
        question: 'iwny, « miroir » (𓉺𓇋𓇋) : lequel de ces trois signes est le signe directeur ?',
        questionEn: 'iwny, "mirror" (𓉺𓇋𓇋): which of these three signs is the lead sign?',
        options: ['𓉺 (le premier signe)', '𓇋 (le deuxième signe)', '𓇋 (le troisième signe)'],
        optionsEn: ['𓉺 (the first sign)', '𓇋 (the second sign)', '𓇋 (the third sign)'],
        correctIndex: 0,
        explanation: '𓉺 (O28, un pilier) est le signe directeur, le même que dans iwn, « An/Ani », épithète d\'Osiris (voir la fiche « iwn » dans Divinités) — le signe-pilier renvoyant à la forme même des miroirs égyptiens, souvent munis d\'un manche en forme de colonne.',
        explanationEn: '𓉺 (O28, a pillar) is the lead sign, the same one found in iwn, "An/Ani", epithet of Osiris (see the "iwn" entry in Divinities) — the pillar-sign echoing the very shape of Egyptian mirrors, often fitted with a column-shaped handle.',
      },
    },
  },
  {
    key: 'Ḥrw',
    word: {
      translation: 'Partie supérieure, sommet, haut',
      translationEn: 'Upper part, summit, top',
      illustration: '𓁷𓂋𓇯',
      quiz: {
        question: 'Ḥrw, « partie supérieure, sommet » (𓁷𓂋𓇯) : lequel de ces trois signes est le signe directeur ?',
        questionEn: 'Ḥrw, "upper part, summit" (𓁷𓂋𓇯): which of these three signs is the lead sign?',
        options: ['𓁷 (le premier signe)', '𓂋 (le deuxième signe)', '𓇯 (le dernier signe, un déterminatif)'],
        optionsEn: ['𓁷 (the first sign)', '𓂋 (the second sign)', '𓇯 (the last sign, a determinative)'],
        correctIndex: 0,
        explanation: '𓁷 (D2, un visage de face) est le signe directeur — la même racine que dans Ḥr, « Horus » (voir la fiche « Ḥr » dans Divinités) : Horus conçu comme « celui d\'en haut ». Pulaar hoore, « tête » ; susu kóóréé, « firmament, ciel ».',
        explanationEn: '𓁷 (D2, a face seen from the front) is the lead sign — the same root as in Ḥr, "Horus" (see the "Ḥr" entry in Divinities): Horus conceived as "the one above". Pulaar hoore, "head"; Susu kóóréé, "firmament, sky".',
      },
    },
  },
  {
    key: 'jmntt',
    word: {
      translation: 'Amenthès – L\'Occident, séjour souterrain des morts',
      translationEn: 'Amentet – The West, the underground abode of the dead',
      illustration: '𓋀𓏏𓈉',
      quiz: {
        question: 'jmntt, « Amenthès », l\'Occident des morts (𓋀𓏏𓈉) : lequel de ces trois signes est le signe directeur ?',
        questionEn: 'jmntt, "Amentet", the West of the dead (𓋀𓏏𓈉): which of these three signs is the lead sign?',
        options: ['𓋀 (le premier signe)', '𓏏 (le deuxième signe)', '𓈉 (le dernier signe, un déterminatif)'],
        optionsEn: ['𓋀 (the first sign)', '𓏏 (the second sign)', '𓈉 (the last sign, a determinative)'],
        correctIndex: 0,
        explanation: '𓋀 (R14) est le signe directeur : c\'est l\'idéogramme du mot « Ouest » lui-même. Un vaste champ bantou de l\'eau (amanzi/maji) rapproche, phonétiquement, Amenthès de l\'eau qu\'il faut traverser pour y accéder.',
        explanationEn: '𓋀 (R14) is the lead sign: it is the ideogram of the word "West" itself. A broad Bantu water field (amanzi/maji) phonetically links Amentet to the water that must be crossed to reach it.',
      },
    },
  },
  {
    key: 'tpy',
    word: {
      translation: 'Premier, qui est sur, en chef',
      translationEn: 'First, foremost, chief',
      illustration: '𓁶𓊪𓏭',
      quiz: {
        question: 'tpy, « premier, qui est en chef » (𓁶𓊪𓏭) : lequel de ces trois signes est le signe directeur ?',
        questionEn: 'tpy, "first, foremost" (𓁶𓊪𓏭): which of these three signs is the lead sign?',
        options: ['𓁶 (le premier signe)', '𓊪 (le deuxième signe)', '𓏭 (le dernier signe)'],
        optionsEn: ['𓁶 (the first sign)', '𓊪 (the second sign)', '𓏭 (the last sign)'],
        correctIndex: 0,
        explanation: '𓁶 (D1, une tête vue de profil) est le signe directeur, dérivé de tp, « tête » — littéralement « ce qui est en tête ». Ce mot est le second élément de sp tpy, « la Première Occasion », la Création (voir la fiche « sp tpy » dans Divinités).',
        explanationEn: '𓁶 (D1, a head seen in profile) is the lead sign, derived from tp, "head" — literally "that which is at the head". This word is the second element of sp tpy, "the First Occasion", the Creation (see the "sp tpy" entry in Divinities).',
      },
    },
  },
  {
    key: 'šmty',
    word: {
      translation: 'Pschent – La double couronne',
      translationEn: 'Pschent – The double crown',
      illustration: '𓌂𓏏𓐍𓏭𓋗',
      quiz: {
        question: 'šmty, « le Pschent », la double couronne d\'Égypte (𓌂𓏏𓐍𓏭𓋗) : lequel de ces cinq signes est le signe directeur ?',
        questionEn: 'šmty, "the Pschent", the double crown of Egypt (𓌂𓏏𓐍𓏭𓋗): which of these five signs is the lead sign?',
        options: ['𓌂 (le premier signe)', '𓏏 (le deuxième signe)', '𓐍 (le troisième signe)', '𓋗 (le dernier signe, un déterminatif)'],
        optionsEn: ['𓌂 (the first sign)', '𓏏 (the second sign)', '𓐍 (the third sign)', '𓋗 (the last sign, a determinative)'],
        correctIndex: 0,
        explanation: '𓌂 (S6) est le signe directeur : il fusionne à lui seul les deux couronnes de Haute et Basse-Égypte en un seul dessin. Le nom grec « pschent » vient du copte ⲡⲓ-ϣⲉⲛⲧ, « la tresse ».',
        explanationEn: '𓌂 (S6) is the lead sign: it alone fuses the two crowns of Upper and Lower Egypt into a single drawing. The Greek name "pschent" comes from Coptic ⲡⲓ-ϣⲉⲛⲧ, "the plait".',
      },
    },
  },
  {
    key: 'swty',
    word: {
      translation: 'Double plume – Coiffe emblématique du dieu Amon',
      translationEn: 'Double plume – Emblematic headdress of the god Amun',
      illustration: '𓆄𓏏𓏭𓋛',
      quiz: {
        question: 'swty, « la double plume », coiffe d\'Amon (𓆄𓏏𓏭𓋛) : lequel de ces quatre signes est le signe directeur ?',
        questionEn: 'swty, "the double plume", Amun\'s headdress (𓆄𓏏𓏭𓋛): which of these four signs is the lead sign?',
        options: ['𓆄 (le premier signe)', '𓏏 (le deuxième signe)', '𓏭 (le troisième signe)', '𓋛 (le dernier signe, un déterminatif)'],
        optionsEn: ['𓆄 (the first sign)', '𓏏 (the second sign)', '𓏭 (the third sign)', '𓋛 (the last sign, a determinative)'],
        correctIndex: 0,
        explanation: '𓆄 (H6, une plume) est le signe directeur — le duel de swt, « plume » (voir la fiche « swt » ci-dessous), redoublé pour former « la double plume ».',
        explanationEn: '𓆄 (H6, a feather) is the lead sign — the dual of swt, "feather" (see the "swt" entry below), doubled to form "the double plume".',
      },
    },
  },
  {
    key: 'swt',
    word: {
      translation: 'Plume',
      translationEn: 'Feather',
      illustration: '𓆄𓅱𓏏𓁸',
      quiz: {
        question: 'swt, « plume » (𓆄𓅱𓏏𓁸) : lequel de ces quatre signes est le signe directeur ?',
        questionEn: 'swt, "feather" (𓆄𓅱𓏏𓁸): which of these four signs is the lead sign?',
        options: ['𓆄 (le premier signe)', '𓅱 (le deuxième signe)', '𓏏 (le troisième signe)', '𓁸 (le dernier signe, un déterminatif)'],
        optionsEn: ['𓆄 (the first sign)', '𓅱 (the second sign)', '𓏏 (the third sign)', '𓁸 (the last sign, a determinative)'],
        correctIndex: 0,
        explanation: '𓆄 (H6) est le signe directeur : bambara sí, « cheveu, poil, plume » ; malinké sí/sígi, « plume ». Ce même signe, redoublé, épelle swty, « la double plume » (voir la fiche « swty » ci-dessus), coiffe emblématique du dieu Amon.',
        explanationEn: '𓆄 (H6) is the lead sign: Bambara sí, "hair, feather"; Malinke sí/sígi, "feather". This same sign, doubled, spells swty, "the double plume" (see the "swty" entry above), the emblematic headdress of the god Amun.',
      },
    },
  },
  {
    key: 'st.t',
    word: {
      translation: 'Trône / Siège',
      translationEn: 'Throne / Seat',
      illustration: '𓊨𓏏',
      quiz: {
        question: 'st.t, « trône, siège » (𓊨𓏏) : lequel de ces deux signes est le signe directeur ?',
        questionEn: 'st.t, "throne, seat" (𓊨𓏏): which of these two signs is the lead sign?',
        options: ['𓊨 (le premier signe)', '𓏏 (le second signe)'],
        optionsEn: ['𓊨 (the first sign)', '𓏏 (the second sign)'],
        correctIndex: 0,
        explanation: '𓊨 (Q1, un trône) est le signe directeur : ce mot autonome est aussi la base du nom d\'Isis (voir la fiche « Ỉs.t » dans Divinités). Copte ⲥⲉ/ⲥⲓ, « siège, place » ; bambara sìgi, « position assise, asseoir ».',
        explanationEn: '𓊨 (Q1, a throne) is the lead sign: this standalone word is also the basis of Isis\'s name (see the "Ỉs.t" entry in Divinities). Coptic ⲥⲉ/ⲥⲓ, "seat, place"; Bambara sìgi, "seated position, to sit".',
      },
    },
  },
  {
    key: 'tmt',
    word: {
      translation: 'Traîneau – Utilisé pour tracter blocs de pierre et statues',
      translationEn: 'Sledge – Used to haul stone blocks and statues',
      illustration: '𓏏𓍃𓅓𓏏𓆱',
      quiz: {
        question: 'tmt, « traîneau » (𓏏𓍃𓅓𓏏𓆱) : lequel de ces cinq signes est le signe directeur ?',
        questionEn: 'tmt, "sledge" (𓏏𓍃𓅓𓏏𓆱): which of these five signs is the lead sign?',
        options: ['𓏏 (le premier signe)', '𓍃 (le deuxième signe)', '𓅓 (le troisième signe)', '𓆱 (le dernier signe, un déterminatif)'],
        optionsEn: ['𓏏 (the first sign)', '𓍃 (the second sign)', '𓅓 (the third sign)', '𓆱 (the last sign, a determinative)'],
        correctIndex: 1,
        explanation: '𓍃 (U15, un traîneau, valeur tm) est le signe directeur — le radical même du nom du dieu Ỉtm/Atoum (voir la fiche « Ỉtm » dans Divinités).',
        explanationEn: '𓍃 (U15, a sledge, value tm) is the lead sign — the very root of the name of the god Ỉtm/Atum (see the "Ỉtm" entry in Divinities).',
      },
    },
  },
  {
    key: 'Ḏḥwty-ms',
    word: {
      translation: 'Thoutmosis – « Né de Thot »',
      translationEn: 'Thutmose – "Born of Thoth"',
      illustration: '𓅝𓄟𓋴',
      quiz: {
        question: 'Ḏḥwty-ms, « Thoutmosis », nom porté par plusieurs pharaons (𓅝𓄟𓋴) : lequel de ces trois signes est le signe directeur ?',
        questionEn: 'Ḏḥwty-ms, "Thutmose", a name borne by several pharaohs (𓅝𓄟𓋴): which of these three signs is the lead sign?',
        options: ['𓅝 (le premier signe)', '𓄟 (le deuxième signe)', '𓋴 (le dernier signe)'],
        optionsEn: ['𓅝 (the first sign)', '𓄟 (the second sign)', '𓋴 (the last sign)'],
        correctIndex: 0,
        explanation: '𓅝 (G26, un ibis) est le signe directeur — la racine du nom du dieu Thot lui-même (voir la fiche « Ḏḥwtj » dans Divinités). Le nom entier, littéralement « né de Thot », combine cette racine avec 𓄟 (F31, ms, « naître », voir la fiche « Msḫnt » dans Divinités).',
        explanationEn: '𓅝 (G26, an ibis) is the lead sign — the root of the god Thoth\'s own name (see the "Ḏḥwtj" entry in Divinities). The full name, literally "born of Thoth", combines this root with 𓄟 (F31, ms, "to be born", see the "Msḫnt" entry in Divinities).',
      },
    },
  },
  {
    key: 'šnti',
    word: {
      translation: 'Acacia, épine d\'acacia',
      translationEn: 'Acacia, acacia thorn',
      illustration: '𓍶𓈖𓍙𓆭',
      quiz: {
        question: 'šnti, « acacia, épine d\'acacia » (𓍶𓈖𓍙𓆭) : lequel de ces quatre signes est le signe directeur ?',
        questionEn: 'šnti, "acacia, acacia thorn" (𓍶𓈖𓍙𓆭): which of these four signs is the lead sign?',
        options: ['𓍶 (le premier signe)', '𓈖 (le deuxième signe)', '𓍙 (le troisième signe)', '𓆭 (le dernier signe, un déterminatif)'],
        optionsEn: ['𓍶 (the first sign)', '𓈖 (the second sign)', '𓍙 (the third sign)', '𓆭 (the last sign, a determinative)'],
        correctIndex: 0,
        explanation: '𓍶 (V9, l\'anneau-šen) est le signe directeur — le même signe que dans šnw, « cartouche » (voir la fiche « šnw-cartouche » dans Objets), šn, « éternité », et šnw, « cheveux ».',
        explanationEn: '𓍶 (V9, the šen-ring) is the lead sign — the same sign found in šnw, "cartouche" (see the "šnw-cartouche" entry in Objects), šn, "eternity", and šnw, "hair".',
      },
    },
  },
  {
    key: 'šnw (acacia)',
    word: {
      translation: 'Acacia, épine d\'acacia (variante)',
      translationEn: 'Acacia, acacia thorn (variant)',
      illustration: '𓍶𓏌𓏏𓆱',
      quiz: {
        question: 'šnw, variante graphique pour « acacia » (𓍶𓏌𓏏𓆱) : lequel de ces quatre signes est le signe directeur ?',
        questionEn: 'šnw, a graphic variant for "acacia" (𓍶𓏌𓏏𓆱): which of these four signs is the lead sign?',
        options: ['𓍶 (le premier signe)', '𓏌 (le deuxième signe)', '𓏏 (le troisième signe)', '𓆱 (le dernier signe, un déterminatif)'],
        optionsEn: ['𓍶 (the first sign)', '𓏌 (the second sign)', '𓏏 (the third sign)', '𓆱 (the last sign, a determinative)'],
        correctIndex: 0,
        explanation: '𓍶 (V9, l\'anneau-šen) est le signe directeur, le même que dans šnti (voir la fiche « šnti » ci-dessus) — une variante graphique du même mot, avec des compléments phonétiques différents.',
        explanationEn: '𓍶 (V9, the šen-ring) is the lead sign, the same one found in šnti (see the "šnti" entry above) — a graphic variant of the same word, with different phonetic complements.',
      },
    },
  },
  {
    key: 'Nḫbt',
    word: {
      translation: 'Germination, pousse',
      translationEn: 'Germination, sprout',
      illustration: '𓈖𓐍𓃀𓏏𓇑',
      quiz: {
        question: 'Nḫbt, « germination, pousse » (𓈖𓐍𓃀𓏏𓇑) : lequel de ces cinq signes est le signe directeur ?',
        questionEn: 'Nḫbt, "germination, sprout" (𓈖𓐍𓃀𓏏𓇑): which of these five signs is the lead sign?',
        options: ['𓈖 (le premier signe)', '𓐍 (le deuxième signe)', '𓃀 (le troisième signe)', '𓇑 (le dernier signe)'],
        optionsEn: ['𓈖 (the first sign)', '𓐍 (the second sign)', '𓃀 (the third sign)', '𓇑 (the last sign)'],
        correctIndex: 3,
        explanation: '𓇑 (M22, un bourgeon) est le signe directeur : bambara nyɛ́bɔ, « produire de la graine », nyɛ́bɔduga, « vautour africain » — le même doublet germination/vautour retrouvé de part et d\'autre sur cette racine.',
        explanationEn: '𓇑 (M22, a bud) is the lead sign: Bambara nyɛ́bɔ, "to produce seed", nyɛ́bɔduga, "African vulture" — the same germination/vulture doublet found elsewhere on this root.',
      },
    },
  },
  {
    key: 'wnw.t',
    word: {
      translation: 'Heure – Mesurée par les clepsydres et les cadrans solaires',
      translationEn: 'Hour – Measured by water clocks and sundials',
      illustration: '𓃹𓈖𓏌𓏏𓇼𓇳',
      quiz: {
        question: 'wnw.t, « heure » (𓃹𓈖𓏌𓏏𓇼𓇳) : lequel de ces six signes est le signe directeur ?',
        questionEn: 'wnw.t, "hour" (𓃹𓈖𓏌𓏏𓇼𓇳): which of these six signs is the lead sign?',
        options: ['Le premier signe, 𓃹', 'Le troisième signe, 𓏌', 'Le cinquième signe, 𓇼', 'Le dernier signe, 𓇳'],
        optionsEn: ['The first sign, 𓃹', 'The third sign, 𓏌', 'The fifth sign, 𓇼', 'The last sign, 𓇳'],
        correctIndex: 0,
        explanation: '𓃹 (E34, un lièvre, valeur wn) est le signe directeur — le même signe que dans wnty, « Ounty », l\'aspect vengeur d\'Horus (voir la fiche « wnty » dans Divinités), les deux mots étant homophones.',
        explanationEn: '𓃹 (E34, a hare, value wn) is the lead sign — the same sign found in wnty, "Wenty", the vengeful aspect of Horus (see the "wnty" entry in Divinities), the two words being homophones.',
      },
    },
  },
  {
    key: 'rnp.t',
    word: {
      translation: 'Année – 365 jours divisés en trois saisons',
      translationEn: 'Year – 365 days divided into three seasons',
      illustration: '𓆳𓏏𓏤',
      quiz: {
        question: 'rnp.t, « année » (𓆳𓏏𓏤) : lequel de ces trois signes est le signe directeur ?',
        questionEn: 'rnp.t, "year" (𓆳𓏏𓏤): which of these three signs is the lead sign?',
        options: ['𓆳 (le premier signe)', '𓏏 (le deuxième signe)', '𓏤 (le dernier signe)'],
        optionsEn: ['𓆳 (the first sign)', '𓏏 (the second sign)', '𓏤 (the last sign)'],
        correctIndex: 0,
        explanation: '𓆳 (M4, une branche de palmier dépouillée) est le signe directeur — l\'instrument égyptien de décompte des années. Copte ⲗⲁⲙⲡⲓ, « année » ; kikongo -lombe, « branche de palmier ».',
        explanationEn: '𓆳 (M4, a stripped palm branch) is the lead sign — the Egyptian instrument for counting years. Coptic ⲗⲁⲙⲡⲓ, "year"; Kikongo -lombe, "palm branch".',
      },
    },
  },
  {
    key: 'pr.t',
    word: {
      translation: 'Péret – Saison des Semailles',
      translationEn: 'Peret – The Sowing season',
      illustration: '𓉐𓂋𓏏𓇳',
      quiz: {
        question: 'pr.t, « Péret », la saison des semailles (𓉐𓂋𓏏𓇳) : lequel de ces quatre signes est le signe directeur ?',
        questionEn: 'pr.t, "Peret", the sowing season (𓉐𓂋𓏏𓇳): which of these four signs is the lead sign?',
        options: ['𓉐 (le premier signe)', '𓂋 (le deuxième signe)', '𓏏 (le troisième signe)', '𓇳 (le dernier signe)'],
        optionsEn: ['𓉐 (the first sign)', '𓂋 (the second sign)', '𓏏 (the third sign)', '𓇳 (the last sign)'],
        correctIndex: 0,
        explanation: '𓉐 (O1, une maison, valeur pr) est le signe directeur — le même signe que dans pr, « maison » (voir la fiche « pr » dans Lire les hiéroglyphes). Copte ⲡⲉⲓⲣⲉ, « apparaître » ; bassa ɓér, « monter » — la terre qui émerge et sur laquelle on plante, à cette saison.',
        explanationEn: '𓉐 (O1, a house, value pr) is the lead sign — the same sign found in pr, "house" (see the "pr" entry in Reading Hieroglyphs). Coptic ⲡⲉⲓⲣⲉ, "to appear"; Bassa ɓér, "to rise" — the land that emerges and on which one plants, in this season.',
      },
    },
  },
  {
    key: 'mḥ',
    word: {
      translation: 'Remplir, compléter, finir',
      translationEn: 'To fill, complete, finish',
      illustration: '𓅓𓎔𓎛',
      quiz: {
        question: 'mḥ, « remplir, compléter, finir » (𓅓𓎔𓎛) : lequel de ces trois signes est le signe directeur ?',
        questionEn: 'mḥ, "to fill, complete, finish" (𓅓𓎔𓎛): which of these three signs is the lead sign?',
        options: ['𓅓 (le premier signe)', '𓎔 (le deuxième signe)', '𓎛 (le dernier signe)'],
        optionsEn: ['𓅓 (the first sign)', '𓎔 (the second sign)', '𓎛 (the last sign)'],
        correctIndex: 1,
        explanation: '𓎔 (V22, un fouet ou une lanière de berger) porte à lui seul la valeur bilitère mḥ : c\'est le signe directeur. Bambara màa, mɔ̀ɔ, mɔ̀gɔ, mɔ̀kɔ, « homme, personne, quelqu\'un » — l\'homme accompli, « rempli » de son humanité.',
        explanationEn: '𓎔 (V22, a whip or shepherd\'s crook) alone carries the biliteral value mḥ: it is the lead sign. Bambara màa, mɔ̀ɔ, mɔ̀gɔ, mɔ̀kɔ, "man, person, someone" — the accomplished man, "filled" with his humanity.',
      },
    },
  },
];

export default signeDirecteurPractice;
